import type { Graphics } from 'pixi.js';
import { smooth } from '../../burst';
import { lerpColor } from '../../color';
import { hsl, type WorldDress, type WorldFx } from '../../world';

const TAU = Math.PI * 2;
const ROSETTE = 18;
const LUGS = 8;
const JINGLES = 8;
const CROWN = 8;
const LIFE = 1.1;
const SHAKE = 0.15;
const LEAF = hsl(160, 85, 76);

function leaf(g: Graphics, x: number, y: number, len: number, angle: number, wide: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const w = len * wide;
  const pts: number[] = [];
  const steps = 6;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const v = w * Math.sin(Math.PI * t) * (1 - 0.3 * t);
    pts.push(x + t * len * c - v * s, y + t * len * s + v * c);
  }
  for (let i = steps - 1; i > 0; i--) {
    const t = i / steps;
    const v = -w * Math.sin(Math.PI * t) * (1 - 0.3 * t);
    pts.push(x + t * len * c - v * s, y + t * len * s + v * c);
  }
  g.poly(pts, true);
}

function jingles(g: Graphics, x: number, y: number, size: number, angle: number): void {
  const tx = -Math.sin(angle) * size * 0.8;
  const ty = Math.cos(angle) * size * 0.8;
  g.circle(x + tx, y + ty, size);
  g.circle(x - tx, y - ty, size);
}

function ageOf(grow: number): number {
  const k = Math.max(0, Math.min(1, grow));
  return (0.5 - Math.sin(Math.asin(1 - 2 * k) / 3)) * LIFE;
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    g.ellipse(body.x, body.y, r * 0.4 * sx, r * 0.4 * sy).fill({ color: lerpColor(color, pal.ink, 0.72), alpha: 0.6 * alpha });
    for (const s of [0.5, 0.68]) g.ellipse(body.x, body.y, r * s * sx, r * s * sy);
    g.stroke({ width: Math.max(1, r * 0.03), color: lerpColor(color, pal.ink, 0.5), alpha: 0.55 * alpha });
    for (let i = 0; i < ROSETTE; i++) {
      const a = (i / ROSETTE) * TAU;
      g.circle(body.x + Math.cos(a) * r * 0.59 * sx, body.y + Math.sin(a) * r * 0.59 * sy, r * (i % 2 === 0 ? 0.05 : 0.032));
    }
    g.fill({ color: lerpColor(color, pal.heldShade, 0.55), alpha: 0.75 * alpha });
    k.highlight(body, 0.55 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const sx = body.scaleX;
    const sy = body.scaleY;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x - r * 0.08 * sx, body.y - r * 0.1 * sy, r * 0.48 * sx, r * 0.44 * sy);
    g.fill({ color: pal.heldLight, alpha: 0.28 * alpha });
    g.ellipse(body.x, body.y, r * 0.86 * sx, r * 0.86 * sy);
    g.stroke({ width: r * 0.17, color: lerpColor(pal.heldShade, pal.ink, 0.18), alpha });
    k.ringDots(body, r * 0.86, LUGS, Math.PI / LUGS, (x, y) => {
      g.circle(body.x + (x - body.x) * sx, body.y + (y - body.y) * sy, r * 0.065);
    });
    g.fill({ color: pal.rim, alpha });
    g.stroke({ width: Math.max(1, r * 0.02), color: pal.ink, alpha: 0.55 * alpha });
    k.highlight(body, 0.45 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    const size = Math.max(2.4, k.unit * 0.013);
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.1 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1, color: pal.spot, alpha: 0.3 * alpha });
    k.ringDots(body, reach, JINGLES, spin, (x, y, _i, a) => {
      jingles(g, x, y, size, a);
    });
    g.fill({ color: lerpColor(pal.spot, pal.rim, 0.35), alpha: 0.85 * alpha });
    k.ringDots(body, reach, JINGLES, spin, (x, y, _i, a) => {
      const dx = -Math.sin(a) * size * 2;
      const dy = Math.cos(a) * size * 2;
      g.moveTo(x - dx, y - dy).lineTo(x + dx, y + dy);
    });
    g.stroke({ width: 1, color: pal.ink, alpha: 0.55 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const tone = last ? pal.spot : pal.incoming.circle;
    if (on) g.circle(x, y, radius * 2).fill({ color: tone, alpha: 0.2 * alpha });
    g.circle(x + radius * 0.3, y - radius * 0.24, radius * 0.92).fill({
      color: on ? lerpColor(tone, pal.ink, 0.4) : lerpColor(pal.bg, pal.rim, 0.1),
      alpha,
    });
    g.circle(x, y, radius).fill({ color: on ? tone : lerpColor(pal.bg, pal.rim, 0.18), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha });
    g.circle(x, y, radius * 0.26).fill({ color: pal.ink, alpha: (on ? 0.6 : 0.35) * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) g.lineTo(points[i]!, points[i + 1]!);
    g.stroke({ width: 1.2, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    const size = Math.max(2, k.unit * 0.008);
    for (let i = 2; i < count; i += 2) {
      const x = (points[i - 2]! + points[i]!) / 2;
      const y = (points[i - 1]! + points[i + 1]!) / 2;
      g.ellipse(x, y, size * 1.5, size);
    }
    g.fill({ color: lerpColor(pal.path, pal.incoming.circle, 0.4), alpha: alpha * 3.2 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      const len = size * 2.6;
      leaf(g, x - Math.cos(rot) * len * 0.5, y - Math.sin(rot) * len * 0.5, len, rot, 0.3);
      g.fill({ color: lerpColor(color, LEAF, 0.6), alpha });
      return;
    }
    g.star(x, y, 4, size * 1.25, size * 0.32, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const age = ageOf(m.grow);
    const shake = smooth(age / SHAKE) * m.unit * 0.014;
    const crown = smooth((age - 0.05) / 0.3);
    const size = Math.max(2.5, m.unit * 0.015);
    if (crown > 0) {
      for (let i = 0; i < CROWN; i++) {
        const a = ((i + 0.5) / CROWN) * TAU;
        const len = m.unit * 0.075 * crown;
        leaf(g, m.x + Math.cos(a) * m.r * 1.06, m.y + Math.sin(a) * m.r * 1.06, len, a, 0.3);
      }
      g.fill({ color: lerpColor(pal.spot, pal.ink, 0.3), alpha: 0.7 * m.fade });
    }
    g.circle(m.x, m.y, m.r).stroke({ width: m.width * 0.6, color: pal.spot, alpha: 0.35 * m.fade });
    for (let i = 0; i < JINGLES; i++) {
      const a = (i / JINGLES) * TAU;
      const d = m.r + Math.sin(age * TAU * 6 + i * Math.PI) * shake;
      jingles(g, m.x + Math.cos(a) * d, m.y + Math.sin(a) * d, size, a);
    }
    g.fill({ color: lerpColor(pal.spot, pal.rim, 0.4), alpha: 0.85 * m.fade });
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size * 0.45);
    g.star(x, y, 4, size * 1.25, size * 0.25, angle);
  },
};
